import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  History,
  Loader2,
  RotateCcw,
  Save,
  X,
} from 'lucide-react';
import {
  promptsApi,
  PromptHistoryRow,
  PromptRow,
} from '../../services/promptsApi';

function formatDateTime(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortKey(key: string): string {
  // "intake.extraction_tail" → "Extraction Tail"
  const last = key.split('.').slice(-1)[0] || key;
  return last
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function PromptsPanel() {
  const [rows, setRows] = useState<PromptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<PromptRow | null>(null);
  const [editorText, setEditorText] = useState('');
  const [editorWho, setEditorWho] = useState('');
  const [saving, setSaving] = useState(false);

  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [history, setHistory] = useState<PromptHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await promptsApi.list();
      setRows(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openEditor = (row: PromptRow) => {
    setEditing(row);
    setEditorText(row.prompt_text);
    setEditorWho('');
  };

  const closeEditor = () => {
    setEditing(null);
    setEditorText('');
    setEditorWho('');
  };

  const handleSave = async () => {
    if (!editing) return;
    if (editorText.trim().length === 0) {
      setError('Prompt text cannot be empty.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await promptsApi.update(
        editing.prompt_key,
        editorText,
        editorWho.trim() || undefined,
      );
      setRows((prev) =>
        prev.map((r) => (r.prompt_key === updated.prompt_key ? updated : r)),
      );
      closeEditor();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (row: PromptRow) => {
    if (
      !window.confirm(
        `Reset "${shortKey(row.prompt_key)}" to the embedded default? ` +
          'The current text will be archived to history first, so this is reversible.',
      )
    ) {
      return;
    }
    setError(null);
    try {
      const updated = await promptsApi.reset(row.prompt_key);
      setRows((prev) =>
        prev.map((r) => (r.prompt_key === updated.prompt_key ? updated : r)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const openHistory = async (key: string) => {
    setHistoryFor(key);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const list = await promptsApi.history(key);
      setHistory(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRollback = async (key: string, historyId: number) => {
    if (
      !window.confirm(
        'Restore this historical version? The current text will be archived first.',
      )
    ) {
      return;
    }
    try {
      const updated = await promptsApi.rollback(key, historyId);
      setRows((prev) =>
        prev.map((r) => (r.prompt_key === updated.prompt_key ? updated : r)),
      );
      setHistoryFor(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const editingIsDefault = useMemo(() => {
    if (!editing) return true;
    return editorText === editing.default_text;
  }, [editing, editorText]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="animate-spin mr-2" size={18} />
        Loading prompts…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800">
        <AlertCircle size={16} className="mt-0.5 shrink-0" />
        <div>
          These prompts drive the LLM that extracts entities, modules, and the
          issue type from incoming JIRA tickets. Add a <strong>do NOT extract</strong>{' '}
          rule to <em>Extraction Tail</em> when a new false-positive class shows
          up. Edits take effect on the very next ticket — every change is
          archived so you can roll back.
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-800">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((row) => {
          const isDefault = row.prompt_text === row.default_text;
          return (
            <div
              key={row.prompt_key}
              className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium text-gray-800">
                      {shortKey(row.prompt_key)}
                    </h4>
                    <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                      {row.prompt_key}
                    </code>
                    {isDefault ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        Default
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        Edited
                      </span>
                    )}
                  </div>
                  {row.description && (
                    <p className="text-sm text-gray-500 mt-1">{row.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    Last updated {formatDateTime(row.updated_at)} by{' '}
                    {row.updated_by || 'unknown'}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => openEditor(row)}
                    className="px-3 py-1.5 text-sm rounded-lg bg-primary-500 text-white hover:bg-primary-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => openHistory(row.prompt_key)}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg"
                    title="View history"
                  >
                    <History size={16} />
                  </button>
                  <button
                    onClick={() => handleReset(row)}
                    disabled={isDefault}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                    title={isDefault ? 'Already at default' : 'Reset to default'}
                  >
                    <RotateCcw size={16} />
                  </button>
                </div>
              </div>
              <pre className="mt-3 p-3 bg-gray-50 border border-gray-100 rounded text-xs text-gray-700 max-h-32 overflow-y-auto whitespace-pre-wrap">
                {row.prompt_text}
              </pre>
            </div>
          );
        })}
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Edit {shortKey(editing.prompt_key)}
                </h3>
                <code className="text-xs text-gray-500">{editing.prompt_key}</code>
              </div>
              <button
                onClick={closeEditor}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {editing.description && (
                <p className="text-sm text-gray-500">{editing.description}</p>
              )}
              <textarea
                value={editorText}
                onChange={(e) => setEditorText(e.target.value)}
                className="w-full font-mono text-sm border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={20}
                spellCheck={false}
              />
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 shrink-0">
                  Updated by
                </label>
                <input
                  type="text"
                  value={editorWho}
                  onChange={(e) => setEditorWho(e.target.value)}
                  placeholder="your-email@company.com (optional)"
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {editingIsDefault && editorText !== editing.prompt_text && (
                <p className="text-xs text-amber-600">
                  This text matches the embedded default — saving will mark
                  the prompt as Default again.
                </p>
              )}
            </div>
            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setEditorText(editing.default_text)}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Reset to embedded default
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={closeEditor}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || editorText === editing.prompt_text}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <Loader2 className="animate-spin" size={14} />
                  ) : (
                    <Save size={14} />
                  )}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History drawer */}
      {historyFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  History — {shortKey(historyFor)}
                </h3>
                <code className="text-xs text-gray-500">{historyFor}</code>
              </div>
              <button
                onClick={() => setHistoryFor(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {historyLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Loading history…
                </div>
              ) : history.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">
                  No previous versions yet — every edit will appear here.
                </p>
              ) : (
                history.map((h) => (
                  <div
                    key={h.history_id}
                    className="border border-gray-200 rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm">
                        <span className="font-medium text-gray-800">
                          {formatDateTime(h.archived_at)}
                        </span>
                        <span className="text-gray-500 ml-2">
                          (was set {formatDateTime(h.updated_at)} by{' '}
                          {h.updated_by || 'unknown'})
                        </span>
                      </div>
                      <button
                        onClick={() => handleRollback(historyFor, h.history_id)}
                        className="px-3 py-1 text-xs rounded-lg border border-gray-300 hover:bg-gray-50"
                      >
                        Restore
                      </button>
                    </div>
                    <pre className="p-2 bg-gray-50 rounded text-xs text-gray-700 max-h-40 overflow-y-auto whitespace-pre-wrap">
                      {h.prompt_text}
                    </pre>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
